/** One storage agent's page: its settings, base folders, folders, recent jobs and enrolment. */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Clapperboard, Download, FolderInput, HardDrive, KeyRound, Pencil, Save, Send, Server, ShieldOff, Trash2 } from 'lucide-react';
import { PRISM_VERSION, olderThan, type AgentBaseStatus, type StorageAgentDTO, type StorageLocationSummary, type StoragePurpose, type WatchFolderDTO } from '@prism/contracts';
import { api, errorDetail } from '../../../api/client';
import { useAgents, useStorage, useWatchFolders } from '../../../api/hooks';
import { queryKeys } from '../../../api/queryKeys';
import { agentRootLabel } from '../../../components/FolderPicker';
import { Modal } from '../../../components/Modal';
import { TableScroll } from '../../../components/TableScroll';
import { StatusDot } from '../../../components/Status';
import { toast } from '../../../components/Toasts';
import { bytes, relative } from '../../../lib/format';
import { packagingLabel, updateHint } from '../../../lib/agent-install';
import { AddLocationDialog } from '../storage/AddLocationDialog';
import { RemoveDialog } from '../storage/StorageDialogs';
import { WatchFolderDialog } from '../WatchFoldersAdmin';
import { EnrollmentInstructions, issueEnrolmentToken } from './AgentEnrollment';
import { DeleteAgentDialog, EditFolderDialog } from './AgentDialogs';
import { quotaUsedLabel } from '../../../lib/quota';

export function StatusPill({ a }: { a: StorageAgentDTO }) {
  if (a.status === 'revoked') return <StatusDot tone="failed">Revoked</StatusDot>;
  if (a.status === 'pending') return <StatusDot tone="partial">Waiting to enrol</StatusDot>;
  return a.connected
    ? <StatusDot tone="ready">Connected</StatusDot>
    : <StatusDot tone="offline">Disconnected</StatusDot>;
}

/**
 * 1.8.1: the agent reaches PRISM over plain http to a host that is not this computer (its last heartbeat said so):
 * its control traffic and token cross the network unencrypted.
 */
export function PlainHttpBadge({ a }: { a: StorageAgentDTO }) {
  if (!a.insecureControl) return null;
  return (
    <StatusDot tone="partial" data-testid="agent-plain-http"
      title="This agent reaches PRISM over plain http, so its token and commands cross the network unencrypted. Give it PRISM's https address.">
      Plain http
    </StatusDot>
  );
}

function BaseLine({ label, env, s, connected, optional }: { label: string; env: string; s: AgentBaseStatus | undefined; connected: boolean; optional?: boolean }) {
  return (
    <div className="loc-row" data-testid={`agent-base-${env}`}>
      <strong style={{ fontWeight: 550, minWidth: 110 }}>{label}</strong>
      <span className="key">{!s ? (optional ? 'Not set (optional)' : 'Not reported yet') : !s.configured ? <>{optional ? 'Not set (optional): add ' : 'Not set: add '}<span className="mono">{env}</span> on the agent</> : s.readable ? `${bytes(s.freeBytes)} free of ${bytes(s.totalBytes)}${s.writable ? '' : ' · read-only'}` : s.error ?? 'Not readable'}</span>
      {s?.configured && <span className={`avail ${s.readable && connected ? 'online' : 'offline'}`}><span className="dot" /></span>}
    </div>
  );
}

const USE_LABEL = (l: StorageLocationSummary) => l.purpose === 'export' ? 'Export location' : l.purpose === 'edit' ? 'Edit location' : l.agentFolder?.mode === 'in_place' ? 'Archive catalogued in place' : 'PRISM-managed storage';

interface AgentJob { id: string; kind: string; status: string; percent: number | null; attempts: number; createdAt: string; finishedAt: string | null; error: string | null; detail: string | null }

/** Everything about one agent in one place. */
export function AgentPane() {
  const { id } = useParams();
  const q = useAgents();
  const storage = useStorage().data?.items ?? [];
  const watch = useWatchFolders().data?.items ?? [];
  const jobs = useQuery({ queryKey: ['agent-jobs', id], queryFn: () => api<{ items: AgentJob[] }>(`/api/v1/agents/${id}/jobs`), refetchInterval: 5000, enabled: Boolean(id) });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const a = q.data?.items.find((x) => x.id === id);
  const [general, setGeneral] = useState({ name: '', site: '' });
  const [adding, setAdding] = useState<StoragePurpose | 'watch' | null>(null);
  const [editing, setEditing] = useState<StorageLocationSummary | null>(null);
  const [removing, setRemoving] = useState<StorageLocationSummary | null>(null);
  const [editingWatch, setEditingWatch] = useState<WatchFolderDTO | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [installing, setInstalling] = useState(false);
  const [installToken, setInstallToken] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => { if (a) setGeneral({ name: a.name, site: a.site }); }, [a?.id, a?.name, a?.site]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!a) return <div className="page">{q.isLoading ? <div className="skeleton" style={{ height: 200 }} /> : <p className="muted">Agent not found. <Link to="/admin/agents">Back to storage agents</Link></p>}</div>;
  const bases = 'bases' in a.capabilities ? a.capabilities.bases : undefined;
  const packaging = 'packaging' in a.capabilities ? a.capabilities.packaging : undefined;
  const folders = storage.filter((l) => l.agent?.id === a.id && l.agentFolder);
  const legacy = storage.filter((l) => l.agent?.id === a.id && l.agentRoot);
  const watched = watch.filter((w) => w.agent?.id === a.id);
  const active = a.status === 'active';
  const dirty = general.name !== a.name || general.site !== a.site;
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: queryKeys.agents() });
    await qc.invalidateQueries({ queryKey: queryKeys.storage() });
    await qc.invalidateQueries({ queryKey: queryKeys.watchFolders() });
  };

  return (
    <div className="page" data-testid="agent-pane">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn ghost sm" onClick={() => navigate('/admin/agents')}><ArrowLeft size={14} /> Storage agents</button>
        <h2 style={{ margin: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}><Server size={18} color="var(--accent-strong)" /> {a.name}</h2>
        <PlainHttpBadge a={a} />
        <StatusPill a={a} />
      </div>

      <div className="agent-pane-grid">
        <section className="panel-card">
          <p className="section-label">General</p>
          <div className="field-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <div className="field"><label htmlFor="ag-name">Name</label><input id="ag-name" className="input" value={general.name} onChange={(e) => setGeneral({ ...general, name: e.target.value })} disabled={a.status === 'revoked'} /></div>
            <div className="field"><label htmlFor="ag-site">Site</label><input id="ag-site" className="input" value={general.site} onChange={(e) => setGeneral({ ...general, site: e.target.value.toLowerCase() })} disabled={a.status === 'revoked'} /></div>
          </div>
          {dirty && <button className="btn primary sm" style={{ alignSelf: 'flex-start' }} data-testid="agent-save" onClick={() => void (async () => {
            try { await api(`/api/v1/agents/${a.id}`, { method: 'PATCH', json: general }); await refresh(); toast('Saved'); } catch (err) { toast(errorDetail(err), 'error'); }
          })()}><Save size={13} /> Save</button>}
          <dl className="kv">
            <dt>Host</dt><dd>{a.hostname ?? '—'}</dd>
            <dt>Agent version</dt><dd data-testid="agent-version">{a.version ? `v${a.version}` : '—'}{a.version && olderThan(a.version, PRISM_VERSION) && <span className="chip" data-testid="agent-update" style={{ marginLeft: 8, color: 'var(--warning)', background: 'var(--warning-soft)' }}>Update this agent (PRISM is v{PRISM_VERSION}){updateHint(packaging) ? `: ${updateHint(packaging)}` : ''}</span>}</dd>
            {packagingLabel(a.capabilities) && <><dt>Installed as</dt><dd data-testid="agent-packaging">{packagingLabel(a.capabilities)}</dd></>}
            <dt>Last seen</dt><dd>{relative(a.lastSeenAt)}</dd>
            <dt>Enrolled</dt><dd>{relative(a.enrolledAt)}</dd>
            <dt>FFmpeg</dt><dd>{'ffmpeg' in a.capabilities ? (a.capabilities.ffmpeg ? a.capabilities.ffmpeg.replace(/^ffmpeg version /, '').split(' ')[0] : 'Not available (transfers only)') : '—'}</dd>
            {'hwEncoders' in a.capabilities && a.capabilities.hwEncoders && a.capabilities.hwEncoders.length > 0 && <><dt>Hardware encoders</dt><dd>{a.capabilities.hwEncoders.join(', ')}</dd></>}
            <dt>Queued jobs</dt><dd>{a.queuedJobs}</dd>
          </dl>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn ghost sm danger" onClick={() => setDeleting(true)} data-testid="agent-delete"><Trash2 size={13} /> Delete agent</button>
          </div>
          {a.status !== 'revoked' && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn sm" onClick={() => void (async () => {
                const t = await issueEnrolmentToken(a);
                if (!t) return;
                setToken(t);
                await refresh();
              })()}><KeyRound size={13} /> New enrolment token</button>
              <button className="btn sm" data-testid="agent-install-update" onClick={() => setInstalling(true)}><Download size={13} /> Install or update…</button>
              <button className="btn ghost sm danger" onClick={() => setRevoking(true)}><ShieldOff size={13} /> Revoke</button>
            </div>
          )}
        </section>

        <section className="panel-card">
          <p className="section-label">Base folders on the server</p>
          <BaseLine label="Storage root" env="PRISM_AGENT_STORAGE_ROOT" s={bases?.storage} connected={a.connected} />
          <BaseLine label="Watch root" env="PRISM_AGENT_WATCH_ROOT" s={bases?.watch} connected={a.connected} />
          <BaseLine label="Export root" env="PRISM_AGENT_EXPORT_ROOT" s={bases?.export} connected={a.connected} optional />
          {/* Agents before 1.0.0-rc.6 report no edit root at all. */}
          {bases && !bases.edit && a.connected
            ? <span className="help" data-testid="agent-edit-too-old">Edit root: update the agent to use edit locations (Send to edit, 1.0.0-rc.6).</span>
            : <BaseLine label="Edit root" env="PRISM_AGENT_EDIT_ROOT" s={bases?.edit} connected={a.connected} optional />}
          <span className="help">These folders are set on the agent’s server, so PRISM can never reach outside them. Everything below is chosen and changed here. New export folders go under the export root when there is one, otherwise under the storage root; existing export folders keep working where they are.</span>
        </section>
      </div>

      <section className="panel-card" style={{ marginTop: 16 }} data-testid="agent-folders">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <p className="section-label" style={{ margin: 0, flex: 1 }}>Folders</p>
          <button className="btn sm" disabled={!active || !bases?.storage?.configured} onClick={() => setAdding('storage')} data-testid="agent-add-storage"><HardDrive size={13} /> Add storage folder</button>
          <button className="btn sm" disabled={!active || !(bases?.storage?.configured || bases?.export?.configured)} onClick={() => setAdding('export')} data-testid="agent-add-export"><Send size={13} /> Add export folder</button>
          <button className="btn sm" disabled={!active || !bases?.watch?.configured} onClick={() => setAdding('watch')} data-testid="agent-add-watch"><FolderInput size={13} /> Add watch folder</button>
          <button className="btn sm" disabled={!active || !bases?.edit?.configured} onClick={() => setAdding('edit')} data-testid="agent-add-edit"><Clapperboard size={13} /> Add edit folder</button>
        </div>
        <TableScroll>
        <table className="table compact">
          <thead><tr><th>Name</th><th>Folder</th><th>Use</th><th>Status</th><th>Files</th><th /></tr></thead>
          <tbody>
            {folders.map((l) => (
              <tr key={l.id} data-testid="agent-folder">
                <td className="wrap" style={{ fontWeight: 550 }}>{l.name}</td>
                <td className="mono wrap" style={{ minWidth: 160 }}>{agentRootLabel(l.agentFolder!.base)}/{l.agentFolder!.path}</td>
                <td className="wrap">{USE_LABEL(l)}{l.roles.length ? <span className="pill accent" style={{ marginLeft: 6 }}>{l.roles.join(', ')}</span> : null}</td>
                <td className="wrap"><StatusDot tone={l.availability === 'online' ? 'ready' : 'offline'}>{l.availability === 'online' ? (l.quota ? quotaUsedLabel(l.quota) : l.capacity ? `${bytes(l.capacity.freeBytes)} free` : 'Online') : l.lastError ?? 'Offline'}</StatusDot></td>
                <td className="muted nowrap">{l.usage.files.toLocaleString()} · {bytes(l.usage.bytes)}</td>
                <td className="actions">
                  <button className="btn ghost sm" onClick={() => setEditing(l)} data-testid="agent-folder-edit"><Pencil size={12} /> Edit</button>
                  <button className="btn ghost sm danger" onClick={() => setRemoving(l)}><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
            {watched.map((w) => (
              <tr key={w.id}>
                <td className="wrap" style={{ fontWeight: 550 }}>{w.name}</td>
                <td className="mono wrap" style={{ minWidth: 160 }}>{w.agent?.path !== null && w.agent?.path !== undefined ? `watch root/${w.agent.path}` : `root ${w.agent?.root}`}</td>
                <td className="wrap">Watch folder{w.enabled ? '' : ' (paused)'}</td>
                <td className="muted wrap">{w.lastError ?? `Last scan ${relative(w.lastScanAt)}`}</td>
                <td className="muted nowrap">{w.counts.ingested} ingested</td>
                <td className="actions"><button className="btn ghost sm" onClick={() => setEditingWatch(w)}><Pencil size={12} /> Edit</button></td>
              </tr>
            ))}
            {folders.length === 0 && watched.length === 0 && <tr><td colSpan={6} className="muted">No folders yet.{active ? '' : ' Add them once the agent is connected.'}</td></tr>}
          </tbody>
        </table>
        </TableScroll>
        {legacy.length > 0 && (
          <>
            <p className="section-label" style={{ marginTop: 14 }}>Named roots (earlier setup)</p>
            {legacy.map((l) => <div className="loc-row" key={l.id}><strong style={{ fontWeight: 550 }}>{l.agentRoot!.name}</strong><span className="key">{l.name} · {{ in_place: 'Catalogued in place', managed: 'PRISM-managed', watch: 'Watch folder', idle: 'Not used' }[l.agentRoot!.mode]}</span><span className={`avail ${l.availability}`}><span className="dot" /></span></div>)}
          </>
        )}
      </section>

      <section className="panel-card" style={{ marginTop: 16 }}>
        <p className="section-label">Recent jobs</p>
        <TableScroll>
        <table className="table compact" data-testid="agent-jobs">
          <thead><tr><th>Job</th><th>File</th><th>Status</th><th>When</th></tr></thead>
          <tbody>
            {(jobs.data?.items ?? []).map((j) => (
              <tr key={j.id}>
                <td className="nowrap">{j.kind}</td>
                <td className="mono faint wrap" style={{ minWidth: 200 }} title={j.detail ?? ''}>{j.detail ?? ''}</td>
                <td className="wrap" style={{ minWidth: 120 }}><StatusDot tone={j.status === 'succeeded' ? 'ready' : j.status === 'failed' ? 'failed' : 'processing'}>{j.status === 'leased' ? `running${j.percent != null ? ` ${Math.round(j.percent)}%` : ''}` : j.status}</StatusDot>{j.error && <div style={{ color: 'var(--danger)', fontSize: 12 }}>{j.error}</div>}</td>
                <td className="muted">{relative(j.finishedAt ?? j.createdAt)}</td>
              </tr>
            ))}
            {jobs.data?.items.length === 0 && <tr><td colSpan={4} className="muted">No jobs yet.</td></tr>}
          </tbody>
        </table>
        </TableScroll>
      </section>

      {(adding === 'storage' || adding === 'export' || adding === 'edit') && <AddLocationDialog purpose={adding} agentId={a.id} onClose={() => { setAdding(null); void refresh(); }} />}
      {adding === 'watch' && <WatchFolderDialog folder={null} agentId={a.id} onClose={() => { setAdding(null); void refresh(); }} />}
      {editingWatch && <WatchFolderDialog folder={editingWatch} onClose={() => { setEditingWatch(null); void refresh(); }} />}
      {editing && <EditFolderDialog loc={editing} agentId={a.id} onClose={() => { setEditing(null); void refresh(); }} />}
      {removing && <RemoveDialog loc={removing} onClose={() => { setRemoving(null); void refresh(); }} />}
      {token && <Modal title="Enrol the agent again" wide onClose={() => setToken(null)} actions={<button className="btn primary" onClick={() => setToken(null)}>Done</button>}><EnrollmentInstructions token={token} roots={a.allowedRoots} reenrol /></Modal>}
      {installing && (
        <Modal title="Install or update" wide onClose={() => { setInstalling(false); setInstallToken(null); }} actions={<button className="btn primary" onClick={() => { setInstalling(false); setInstallToken(null); }}>Done</button>}>
          <EnrollmentInstructions token={installToken} roots={a.allowedRoots} reenrol agent={a} onCreateToken={async () => {
            try {
              const t = await issueEnrolmentToken(a);
              if (t) { setInstallToken(t); await refresh(); }
            } catch (err) { toast(errorDetail(err), 'error'); }
          }} />
        </Modal>
      )}
      {deleting && <DeleteAgentDialog agent={a} onClose={() => setDeleting(false)} onDeleted={() => { void refresh(); navigate('/admin/agents'); }} />}
      {revoking && (
        <Modal title={`Revoke ${a.name}?`} onClose={() => setRevoking(false)}
          actions={<><button className="btn ghost" onClick={() => setRevoking(false)}>Cancel</button><button className="btn danger" onClick={async () => {
            await api(`/api/v1/agents/${a.id}/revoke`, { method: 'POST', json: {} });
            await refresh();
            toast(`${a.name} revoked`);
            setRevoking(false);
          }}><ShieldOff size={14} /> Revoke agent</button></>}>
          <p style={{ margin: 0 }}>The agent is locked out immediately and its queued jobs fail. Assets on it stay in the catalog with their proxies; their on-site originals show as offline.</p>
        </Modal>
      )}
    </div>
  );
}
